'use client'

/**
 * /forgot-password — request a password-reset email (client island, #257).
 *
 * Posts to Better Auth's /api/auth/request-password-reset, which on vrs.racing
 * is served by the auth-server module (lib/authServer), not by Next — the
 * host/path allowlist in lib/authServer/hostRouting.cjs claims /api/auth/*.
 *
 * ── THE `redirectTo` CONTRACT ────────────────────────────────────────────────
 * Better Auth does NOT email a link to this app directly. It emails
 *
 *     {baseURL}/api/auth/reset-password/{token}?callbackURL={redirectTo}
 *
 * and that endpoint validates the token, then 302s to
 * `{redirectTo}?token={token}` (or `{redirectTo}?error=INVALID_TOKEN` when the
 * link is stale). So `redirectTo` must be OUR /reset-password page, and the
 * token arrives there as a query param. Verified end-to-end against the real
 * handler in scripts/authServer/passwordReset.test.mjs.
 *
 * It is built from window.location.origin rather than hard-coded: the same page
 * must work on vrs.racing and on a preview/candidate origin, and Better Auth's
 * originCheck middleware validates `redirectTo` against trustedOrigins — so a
 * hard-coded production URL would 403 everywhere else.
 *
 * ── ANTI-ENUMERATION ─────────────────────────────────────────────────────────
 * The endpoint deliberately answers identically for a known and an unknown
 * address. This UI must not undo that: on ANY 200 we show one fixed message and
 * never branch on the response body. We also do not reveal a network-level
 * failure differently per address — the only distinct state is "the request
 * itself failed", which is address-independent.
 */

import React, { useState } from 'react'
import styles from '../../styles/passwordReset.module.css'

// The one message shown after any accepted request. Identical for every address
// — see the anti-enumeration note above.
const SENT_NOTICE =
  'If that email address has a VRS account, we have sent it a link to choose a new password. The link expires in 15 minutes.'

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  async function onSubmit(e) {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // Same-origin, so the cookie/origin checks are satisfied naturally.
        body: JSON.stringify({ email, redirectTo: `${window.location.origin}/reset-password` }),
      })
      if (res.ok) {
        setSent(true)
        return
      }
      // A non-200 here is a SERVICE problem (rate limit, misconfiguration), not
      // "no such user" — Better Auth returns 200 for an unknown address. So the
      // copy stays address-independent.
      setError('We could not send that email right now. Please try again in a moment.')
    } catch {
      setError('We could not reach the server. Please check your connection and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.eyebrow}>Account recovery</div>
        <h1 className={styles.h1}>Forgot your password?</h1>

        {sent ? (
          <>
            <p className={styles.notice}>{SENT_NOTICE}</p>
            <p className={styles.finePrint}>
              Didn&apos;t get it? Check your spam folder, or{' '}
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => {
                  setSent(false)
                  setError('')
                }}
              >
                try another address
              </button>
              .
            </p>
          </>
        ) : (
          <>
            <p className={styles.lede}>
              Enter the email address on your VRS account and we&apos;ll send you a link to choose a new one.
            </p>
            <form onSubmit={onSubmit} noValidate={false}>
              <div className={styles.field}>
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <button type="submit" className={styles.submit} disabled={submitting || !email}>
                {submitting ? 'Sending…' : 'Send reset link'}
              </button>
              {error ? <p className={styles.errorMsg}>{error}</p> : null}
            </form>
            {/* /login route (App Router) */}
            <p className={styles.finePrint}>
              Remembered it? <a href="/login">Back to sign in</a>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
